/**
 * @file Submission read-only / review UI components — lifecycleReadonlySummary.ts.
 */

import type { AnyObj } from "./readonlyFormDisplay";
import { formatCompoundUnitSymbol } from "../../utils/formatUnitSymbol";

function summaryNumOrNull(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function pickStageSummary(
  submission: AnyObj,
  camel: string,
  snake: string
): AnyObj | null {
  const direct = submission[camel] ?? submission[snake];
  return direct && typeof direct === "object" ? (direct as AnyObj) : null;
}

function readBackgroundFinishedQtyForSummary(submission: AnyObj): number | null {
  const bg = submission.background as AnyObj | undefined;
  const raw = bg?.finishedQuantity ?? bg?.finished_quantity ?? "";
  const q = parseFloat(String(raw ?? "").replace(/,/g, "").trim());
  return Number.isFinite(q) && q > 0 ? q : null;
}

/** Exported function — sum A1Energy Mj For Summary. */
export function sumA1EnergyMjForSummary(submission: AnyObj): number {
  const rows = submission.A1_Energy;
  if (!Array.isArray(rows)) return 0;
  return rows.reduce((acc, row) => {
    const r = row as AnyObj;
    const kwh = summaryNumOrNull(r.embodiedEnergyKwh ?? r.embodied_energy_kwh);
    if (kwh != null && kwh > 0) return acc + kwh * 3.6;

    const ee = summaryNumOrNull(r.embodiedEnergy ?? r.embodied_energy);
    if (ee == null || ee <= 0) return acc;

    const unitRaw = String(
      r.embodiedEnergyUnit ??
        r.embodied_energy_unit ??
        r.unit ??
        r.unitId ??
        r.unit_id ??
        ""
    )
      .trim()
      .toLowerCase();
    if (unitRaw === "kwh" || unitRaw.includes("kilowatt")) return acc + ee * 3.6;
    return acc + ee;
  }, 0);
}

/** @deprecated Use sumA1EnergyMjForSummary; kept for legacy callers. */
export function sumA1EnergyKwhForSummary(submission: AnyObj): number {
  const mj = sumA1EnergyMjForSummary(submission);
  return mj > 0 ? Math.round((mj / 3.6) * 1000) / 1000 : 0;
}

/** Exported function — sum A1Carbon Reference For Summary. */
export function sumA1CarbonReferenceForSummary(submission: AnyObj): number {
  const rows = submission.A1_Carbon;
  if (!Array.isArray(rows)) return 0;
  let ref = 0;
  let fn = 0;
  for (const row of rows) {
    const r = row as AnyObj;
    const rq = summaryNumOrNull(r.referenceQty ?? r.reference_qty);
    if (rq != null) ref += rq;
    const fu = summaryNumOrNull(
      r.functionalUnitCarbon ?? r.functional_unit_carbon
    );
    if (fu != null) fn += fu;
  }
  if (ref > 0) return Math.round(ref * 1e6) / 1e6;
  return Math.round(fn * 1e6) / 1e6;
}

/** Exported function — build A1Summary Readonly Object. */
export function buildA1SummaryReadonlyObject(submission: AnyObj): AnyObj | null {
  const raw = pickStageSummary(submission, "a1Summary", "a1_summary");
  const energySummary = pickStageSummary(
    submission,
    "energySummary",
    "energy_summary"
  );

  let energyVal = summaryNumOrNull(
    raw?.embodiedEnergyValue ??
      raw?.embodied_energy_value ??
      raw?.embodiedEnergyA1 ??
      raw?.embodied_energy_a1 ??
      energySummary?.embodiedEnergyA1 ??
      energySummary?.embodied_energy_a1
  );

  let energyUnit =
    String(
      raw?.embodiedEnergyUnit ??
        raw?.embodied_energy_unit ??
        energySummary?.unit ??
        "MJ"
    ).trim() || "MJ";

  let carbonVal = summaryNumOrNull(
    raw?.embodiedCarbonValue ??
      raw?.embodied_carbon_value ??
      raw?.embodiedCarbonA1 ??
      raw?.embodied_carbon_a1
  );

  let carbonUnit =
    String(
      raw?.embodiedCarbonUnit ?? raw?.embodied_carbon_unit ?? "kg CO₂e"
    ).trim() || "kg CO₂e";

  const finishedQty = readBackgroundFinishedQtyForSummary(submission);
  const totalEnergy = sumA1EnergyMjForSummary(submission);
  const totalCarbonRef = sumA1CarbonReferenceForSummary(submission);

  if (energyVal == null && finishedQty != null && totalEnergy > 0) {
    energyVal = Math.round((totalEnergy / finishedQty) * 1000) / 1000;
  } else if (energyVal == null && totalEnergy > 0) {
    energyVal = Math.round(totalEnergy * 1000) / 1000;
  }

  if (carbonVal == null && finishedQty != null && totalCarbonRef > 0) {
    carbonVal = Math.round((totalCarbonRef / finishedQty) * 1000) / 1000;
  } else if (carbonVal == null && totalCarbonRef > 0) {
    carbonVal = Math.round(totalCarbonRef * 1000) / 1000;
  }

  if (energyVal == null && carbonVal == null) return null;

  return {
    embodiedEnergyValue: energyVal ?? "",
    embodiedEnergyUnit: formatCompoundUnitSymbol(energyUnit),
    embodiedCarbonValue: carbonVal ?? "",
    embodiedCarbonUnit: formatCompoundUnitSymbol(carbonUnit),
  };
}
